import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'App/styled';
import { useTheme } from 'emotion-theming';
import KebabMenuIcon from 'components/Icons/Account';
import LoggedInUser from './LoggedInUser';
import GuestUser from './GuestUser';
import { BODY_OVERFLOW_FOR_MODAL } from 'shared/constants/dom';
import { Region } from 'types';
import { useIsConfigHeader, useIsThemeHeader } from 'hooks/header';

const Wrapper = styled.div`
  position: relative;
`;

const Button = styled.button`
  background: transparent;
  border: none;
  display: flex;
  align-content: center;
  width: 48px;
  height: 48px;
  padding: 10px;
`;

interface DropDownProps {
  textColor: string;
}

const LIST_ITEM_CLASS = 'list-item';

const DropDown = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  ${({ theme }) => theme.typography.bodyLarge};
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.2);
  z-index: 5;

  .list {
    position: absolute;
    top: 55px;
    right: 13px;
    display: inline-block;
    list-style: none;
    padding: 0;
    min-width: 160px;
    background: #fff;
    border-radius: 3px;
    .list-item {
      display: flex;
      border-bottom: 1px solid #f5f5f5;
      &:last-child {
        border-bottom: none;
      }
    }

    .link {
      display: flex;
      align-items: center;
      padding: 3px 20px;
      text-decoration: none;
      height: 44px;
      color: ${({ textColor }: DropDownProps) => textColor};
    }
  }
`;

interface KebabMenuProps {
  email?: string;
  isSmartLockActive: boolean;
  isAutoSignUp: boolean;
  showChatNow: boolean;
  showVideoConsulation: boolean;
  region: Region;
  setShowZendeskChat: React.Dispatch<React.SetStateAction<boolean>>;
}

function KebabMenu({
  email,
  isSmartLockActive,
  isAutoSignUp,
  showChatNow,
  showVideoConsulation,
  region,
  setShowZendeskChat,
}: KebabMenuProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const dropDownRef = useRef(null);
  const isLoggedIn = !!email;
  const theme: Theme = useTheme();
  const isThemeHeader = useIsThemeHeader();
  const { headerTextColor } = useIsConfigHeader();
  const toggleMenu = () => {
    const isOpen = !isMenuOpen;
    if (isOpen) {
      document.body.classList.add(BODY_OVERFLOW_FOR_MODAL);
    } else {
      document.body.classList.remove(BODY_OVERFLOW_FOR_MODAL);
    }
    setIsMenuOpen(isOpen);
  };

  const handleClickInDropDown = (event: React.MouseEvent) => {
    const isOutsideChild = event.target === event.currentTarget;
    if (isOutsideChild) {
      event.preventDefault();
      toggleMenu();
    }
  };

  return (
    <Wrapper>
      <Button
        type="button"
        onClick={toggleMenu}
        aria-label="Profile"
      >
        <KebabMenuIcon
          isLight={isThemeHeader}
          color={headerTextColor}
        />
      </Button>
      {isMenuOpen &&
        createPortal(
          // ? "tabIndex" is needed here to track the focus and blur of the menu dropdown
          // ! DO NOT remove this unless there's a better logic in place.
          <DropDown
            tabIndex={0}
            ref={dropDownRef}
            textColor={theme.darkGrey}
            onClick={handleClickInDropDown}
          >
            <ul className="list">
              {isLoggedIn ? (
                <LoggedInUser
                  listItemClass={LIST_ITEM_CLASS}
                  closeMenu={() => toggleMenu()}
                  showVideoConsulation={showVideoConsulation}
                  setShowZendeskChat={setShowZendeskChat}
                />
              ) : (
                <GuestUser
                  listItemClass={LIST_ITEM_CLASS}
                  toggleMenu={toggleMenu}
                  isSmartLockActive={isSmartLockActive}
                  isAutoSignUp={isAutoSignUp}
                  showChatNow={showChatNow}
                  closeMenu={() => toggleMenu()}
                  region={region}
                  setShowZendeskChat={setShowZendeskChat}
                />
              )}
            </ul>
          </DropDown>,
          document.body
        )}
    </Wrapper>
  );
}
export default KebabMenu;
